import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import AdminLayout from "../components/AdminLayout";
import { useAuth } from "../context/AuthContext";
import { Plus, Edit2, Trash2, X, Save, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ["general", "orders", "delivery", "quality", "products"];
const emptyFaq = { question_sq: "", question_en: "", answer_sq: "", answer_en: "", category: "general", order: 0, active: true };

const FaqForm = ({ faq, onClose, onSaved, authHeaders }) => {
  const [data, setData] = useState(faq || emptyFaq);
  const [saving, setSaving] = useState(false);
  const isEdit = !!faq?.id;
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...data };
      delete payload.id; delete payload.created_at;
      if (isEdit) {
        await axios.put(`${API}/admin/faqs/${faq.id}`, payload, { headers: authHeaders() });
        toast.success("FAQ u përditësua");
      } else {
        await axios.post(`${API}/admin/faqs`, payload, { headers: authHeaders() });
        toast.success("FAQ u shtua");
      }
      onSaved(); onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gabim");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto py-8 px-4" data-testid="faq-form-modal">
      <form onSubmit={onSubmit} className="bg-white w-full max-w-2xl">
        <div className="flex items-center justify-between p-6 border-b border-zinc-200 sticky top-0 bg-white">
          <h2 className="text-xl font-bold" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {isEdit ? "Ndrysho FAQ" : "Shto FAQ"}
          </h2>
          <button type="button" onClick={onClose} className="p-2 hover:bg-zinc-100"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Pyetja (SQ) *</label>
              <input data-testid="faq-q-sq" required value={data.question_sq} onChange={(e) => set("question_sq", e.target.value)} className="w-full border border-zinc-300 px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Question (EN) *</label>
              <input data-testid="faq-q-en" required value={data.question_en} onChange={(e) => set("question_en", e.target.value)} className="w-full border border-zinc-300 px-3 py-2" />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Përgjigja (SQ)</label>
              <textarea rows={4} value={data.answer_sq} onChange={(e) => set("answer_sq", e.target.value)} className="w-full border border-zinc-300 px-3 py-2 resize-none" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Answer (EN)</label>
              <textarea rows={4} value={data.answer_en} onChange={(e) => set("answer_en", e.target.value)} className="w-full border border-zinc-300 px-3 py-2 resize-none" />
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Kategoria</label>
              <select value={data.category} onChange={(e) => set("category", e.target.value)} className="w-full border border-zinc-300 px-3 py-2">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Renditja</label>
              <input type="number" value={data.order} onChange={(e) => set("order", parseInt(e.target.value) || 0)} className="w-full border border-zinc-300 px-3 py-2" />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={data.active} onChange={(e) => set("active", e.target.checked)} />
                <span className="text-sm font-medium">Aktiv</span>
              </label>
            </div>
          </div>
        </div>

        <div className="flex gap-3 p-6 border-t border-zinc-200 sticky bottom-0 bg-white">
          <button type="button" onClick={onClose} className="flex-1 px-6 py-3 border border-zinc-300 hover:bg-zinc-50">Anulo</button>
          <button data-testid="faq-save" type="submit" disabled={saving} className="flex-1 px-6 py-3 bg-[#0A0A0A] text-white font-medium hover:bg-zinc-800 disabled:opacity-50 flex items-center justify-center gap-2">
            <Save className="w-4 h-4" /> {saving ? "Duke ruajtur..." : "Ruaj"}
          </button>
        </div>
      </form>
    </div>
  );
};

const AdminFAQ = () => {
  const { authHeaders } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/faqs`, { headers: authHeaders() });
      setItems(data);
    } catch { toast.error("Gabim"); }
    finally { setLoading(false); }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  const onDelete = async (f) => {
    if (!window.confirm("Fshi këtë FAQ?")) return;
    try {
      await axios.delete(`${API}/admin/faqs/${f.id}`, { headers: authHeaders() });
      toast.success("U fshi"); load();
    } catch { toast.error("Gabim"); }
  };

  const toggleActive = async (f) => {
    try {
      await axios.put(`${API}/admin/faqs/${f.id}`, { ...f, active: !f.active }, { headers: authHeaders() });
      load();
    } catch { toast.error("Gabim"); }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-faq-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-[#0A0A0A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Pyetjet (FAQ)</h1>
            <p className="text-zinc-600 mt-1">Menaxho pyetjet e shpeshta</p>
          </div>
          <button data-testid="add-faq-button" onClick={() => setEditing({})} className="bg-[#0A0A0A] text-white px-5 py-3 font-medium hover:bg-zinc-800 flex items-center gap-2">
            <Plus className="w-4 h-4" /> Shto Pyetje
          </button>
        </div>

        {loading ? <div className="text-zinc-500">Duke ngarkuar...</div> : (
          <div className="space-y-3">
            {items.map((f) => (
              <div key={f.id} className="bg-white border border-zinc-200 p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="text-xs uppercase tracking-wider text-zinc-500 bg-zinc-100 px-2 py-0.5">{f.category}</span>
                      <span className="text-xs text-zinc-400">#{f.order}</span>
                      <button onClick={() => toggleActive(f)} className={`text-xs px-2 py-0.5 inline-flex items-center gap-1 ${f.active ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"}`}>
                        {f.active ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                        {f.active ? "Aktiv" : "I fshehur"}
                      </button>
                    </div>
                    <p className="font-semibold text-[#0A0A0A]">{f.question_sq}</p>
                    <p className="text-xs text-zinc-500 mb-2">{f.question_en}</p>
                    <p className="text-sm text-zinc-700">{f.answer_sq}</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setEditing(f)} className="p-2 hover:bg-zinc-100"><Edit2 className="w-4 h-4" /></button>
                    <button onClick={() => onDelete(f)} className="p-2 hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            ))}
            {items.length === 0 && <div className="bg-white border border-zinc-200 p-12 text-center text-zinc-500">Asnjë pyetje</div>}
          </div>
        )}

        {editing !== null && (
          <FaqForm faq={editing.id ? editing : null} authHeaders={authHeaders} onClose={() => setEditing(null)} onSaved={load} />
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminFAQ;
